import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Credits.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CreditTransactions = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filters, setFilters] = useState({ type: 'all', status: 'all', from: '', to: '' });

  const fetchData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('marilink_token');
      const qs = new URLSearchParams({ page, limit: 20 });
      if (filters.type !== 'all') qs.append('type', filters.type);
      if (filters.status !== 'all') qs.append('status', filters.status);
      if (filters.from) qs.append('from', filters.from);
      if (filters.to) qs.append('to', filters.to);

      const res = await fetch(`${API_BASE}/credits/transactions?${qs}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) {
        setTransactions(data.transactions || []);
        setPagination(data.pagination);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, filters]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const badgeClass = (s) => ({
    completed: 'completed', pending: 'pending', failed: 'failed',
    refunded: 'refunded', cancelled: 'cancelled', reversed: 'reversed'
  }[s] || 'pending');

  return (
    <div className="cr-page">
      <div className="cr-header">
        <div>
          <h1><i className="fas fa-history"></i> Transaction History</h1>
          <p>Complete ledger of all your credit transactions</p>
        </div>
        <button className="cr-btn cr-btn-secondary" onClick={() => navigate('/credits')}>
          <i className="fas fa-arrow-left"></i> Back to Wallet
        </button>
      </div>

      <div className="cr-filters">
        <select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })}>
          <option value="all">All Types</option>
          <option value="CREDIT_PURCHASE">Credit Purchase</option>
          <option value="CREDIT_USAGE">Credit Usage</option>
          <option value="CREDIT_REFUND">Credit Refund</option>
          <option value="ADMIN_CREDIT">Admin Credit</option>
          <option value="ADMIN_DEBIT">Admin Debit</option>
          <option value="CREDIT_ADJUSTMENT">Adjustment</option>
        </select>
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
          <option value="cancelled">Cancelled</option>
          <option value="reversed">Reversed</option>
        </select>
        <input type="date" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })} />
        <input type="date" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })} />
        {(filters.type !== 'all' || filters.status !== 'all' || filters.from || filters.to) && (
          <button className="cr-btn cr-btn-secondary" onClick={() => setFilters({ type: 'all', status: 'all', from: '', to: '' })}>
            Clear Filters
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
          {pagination.total} transactions
        </span>
      </div>

      {loading ? (
        <div className="cr-loading"><i className="fas fa-spinner fa-spin"></i></div>
      ) : transactions.length === 0 ? (
        <div className="cr-empty">
          <i className="fas fa-inbox"></i>
          <h3>No transactions found</h3>
          <p>Try adjusting your filters</p>
        </div>
      ) : (
        <>
          <div className="cr-table-wrap">
            <table className="cr-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Credits</th>
                  <th>Balance After</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx._id}>
                    <td><span className="cr-tx-id">{tx.transactionId}</span></td>
                    <td>{tx.type.replace(/_/g, ' ')}</td>
                    <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.description || '—'}
                    </td>
                    <td>
                      <span className={tx.credits > 0 ? 'cr-tx-positive' : 'cr-tx-negative'}>
                        {tx.credits > 0 ? '+' : ''}{tx.credits}
                      </span>
                    </td>
                    <td>{tx.balanceAfter}</td>
                    <td><span className={`cr-badge ${badgeClass(tx.status)}`}>{tx.status}</span></td>
                    <td>{new Date(tx.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="cr-pagination">
              <button className="cr-page-btn" disabled={pagination.page === 1} onClick={() => fetchData(pagination.page - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(pagination.page - 2, pagination.totalPages - 4));
                const pageNum = start + i;
                return (
                  <button
                    key={pageNum}
                    className={`cr-page-btn ${pagination.page === pageNum ? 'active' : ''}`}
                    onClick={() => fetchData(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button className="cr-page-btn" disabled={pagination.page === pagination.totalPages} onClick={() => fetchData(pagination.page + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CreditTransactions;